import React, { useState, useRef, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack
} from "@mui/material";
import { handleSubmit } from "../../Functions";
import { useAppContext } from "../../context";
import Loader from "../Loader";

const OTP_LENGTH = 4;

export default function OtpCode({ personalId, mobileNumber, retrySend }: { personalId: string, mobileNumber: string, retrySend: number }) {
  const { setPopupContent, setErrorMsg, setResHeaderHash } = useAppContext();

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(retrySend);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [loading, setLoading] = useState(false);

  // countdown timer
  useEffect(() => {
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // auto jump forward
    if (value && index < OTP_LENGTH - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    // backspace jump back
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  // paste OTP
  const handlePaste = (e: React.ClipboardEvent) => {
    const paste = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (!/^\d+$/.test(paste)) return;

    const newOtp = paste.split("");
    setOtp([...newOtp, ...Array(OTP_LENGTH - newOtp.length).fill("")]);

    inputs.current[newOtp.length - 1]?.focus();
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      setTimer(retrySend);
      setOtp(Array(OTP_LENGTH).fill(""));
      inputs.current[0]?.focus();

      const res = await handleSubmit({ personalId, mobileNumber }, '/api/Account/VerifyClient');
      localStorage.setItem("verificationHash", res?.hash || null);

    } catch {
      setErrorMsg({ title: "შეცდომა", message: "კოდის გაგზავნა ვერ მოხერხდა, გთხოვთ სცადოთ თავიდან", link: "" });
      setPopupContent("ErrorMessage");
    } finally {
      setLoading(false);
    }

    // setResHeaderHash({ 'X-Verification-Hash': res?.hash || null });
  };



  const formatTime = (sec: number) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {

      const res = await handleSubmit({ otp: otp.join(""), personalId, mobileNumber }, '/api/Account/VerifyOtp');
      localStorage.setItem("verificationHash", res?.hash || null);
      // setResHeaderHash({ 'X-Verification-Hash': res?.hash || null });

      const raw = res?.error?.data || res?.data;

      let parsed = null;
      let errorMessage = null;

      if (raw) {
        parsed = JSON.parse(raw);

        if (parsed?.data?.error_message) {
          errorMessage = JSON.parse(parsed.data.error_message);
        }
      }

      if (errorMessage) {
        const errorText = { title: "შეცდომა", message: "გენერირება ჯერ შეუძლებელია", link: "" };
        setErrorMsg({ title: errorText.title, message: errorText.message, link: errorText.link ?? "" });
        setPopupContent("ErrorMessage");
      } else if (parsed?.success) {
        setPopupContent("Dialog");
      }

    } catch (error) {
      setErrorMsg({ title: "შეცდომა", message: "ვერიფიკაცია ვერ შესრულდა, გთხოვთ სცადოთ თავიდან", link: "" });
      setPopupContent("ErrorMessage");

    } finally {
      setLoading(false);
    }

  };


  return (
    <>
      {loading && <Loader />}
      <Paper sx={{ p: 3, borderRadius: 2, sm: 420, md: 410 }}>
        <form onSubmit={handleSendOtp}>
          <Typography variant="h6" fontWeight={600}>
            ვერიფიკაცია
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            შეიყვანეთ თქვენთვის გამოგზავნილი კოდი
          </Typography>

          {/* OTP inputs */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            onPaste={handlePaste}
          >
            {otp.map((digit, index) => (
              <TextField
                key={index}
                value={digit}
                inputRef={(el) => (inputs.current[index] = el)}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                inputProps={{
                  maxLength: 1,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  autoComplete: "one-time-code", // SMS autofill
                  style: {
                    textAlign: "center",
                    fontSize: 20,
                    padding: "10px"
                  }
                }}
                autoComplete="off"
                sx={{
                  width: 45
                }}
              />
            ))}
          </Stack>

          {/* timer */}
          <Typography
            textAlign="center"
            fontSize={13}
            mt={2}
            color="text.secondary"
          >
            {timer > 0 ? (
              <>კოდის ხელახლა გაგზავნა: <b>{formatTime(timer)}</b></>
            ) : (
              <Button
                onClick={resendCode}
                size="small"
                sx={{ textTransform: "none" }}
              >
                კოდის ხელახლა გაგზავნა
              </Button>
            )}
          </Typography>

          {/* submit */}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{
              mt: 3,
              background: "#FF5440"
            }}
          >
            დადასტურება
          </Button>
        </form>
      </Paper>
    </>

  );
}